/**
 * Ce qui reste à faire à l'assistante maternelle sur la page des pièces justificatives.
 *
 * Un document remplacé après un refus repart sans verdict : il n'est ni validé ni refusé tant que
 * l'analyse n'a pas tranché. C'est cet état intermédiaire qui retient « Suivant », et non le statut
 * du dossier, qui reste `documents_rejected` entre-temps — sans quoi la page resterait muette
 * pendant que l'AM attend.
 */
import type { IperiaDocument, IperiaStatusPayload } from '../types/iperiaStatus'

export const rejectedRequiredDocuments = (documents: IperiaDocument[]): IperiaDocument[] =>
  documents.filter((document) => document.required && document.is_rejected)

export const hasDocumentAwaitingVerdict = (documents: IperiaDocument[]): boolean =>
  documents.some(
    (document) => document.required && document.uploaded && !document.is_validated && !document.is_rejected,
  )

/** A verdict on a file just uploaded usually lands within seconds: check often. */
export const DOCUMENT_VERDICT_POLL_INTERVAL_MS = 5_000
/** A review handled by a person can take longer: keep an eye on it, less often. */
export const DOCUMENTS_REVIEW_POLL_INTERVAL_MS = 20_000

/**
 * How often the documents page should refresh, or `null` when nothing is pending.
 *
 * Driven by the documents themselves, not only by the dossier status: on the second sale of a
 * lot the RIB comes mirrored from the first, the payslip is uploaded while the dossier is still
 * `awaiting_am_documents`, and a page that only polled `documents_under_review` stayed on
 * « En attente » until the AM reopened the email link.
 */
export const documentsReviewPollInterval = (status: IperiaStatusPayload | null): number | null => {
  if (!status) return null
  if (hasDocumentAwaitingVerdict(status.documents)) return DOCUMENT_VERDICT_POLL_INTERVAL_MS
  if (['documents_under_review', 'documents_rejected'].includes(status.workflow_status)) {
    return DOCUMENTS_REVIEW_POLL_INTERVAL_MS
  }
  return null
}
